import { Link } from 'react-router-dom'
import {
  ArrowRight, CheckCircle2, Download, FileSignature, FileText, Gift, HardDrive, Layers, ListChecks,
  Lock, PenLine, Repeat, Ruler, ScanSearch, ShieldCheck, Sparkles, Target, TrendingUp, UserRoundX, Zap,
} from 'lucide-react'
import { Layout, SponsorSlot } from '@/components/layout/Layout'
import { CookieChoicesButton } from '@/components/layout/ConsentBanner'
import { BeforeAfter } from '@/components/ui'
import { PricingComparison } from '@/components/marketing/PricingComparison'
import { LocalUsage } from '@/components/marketing/LocalUsage'
import { BulletExample, HeroPreview } from '@/components/marketing/HomePreview'
import { CompanyShowcase } from '@/components/marketing/CompanyShowcase'
import { RULE_CHECKS, SCORE_CATEGORIES } from '@/data/homeSample'
import { useSeo } from '@/seo/useSeo'
import { FaqSection } from '@/components/marketing/FaqSection'
import { STATIC_FAQS } from '@/seo/staticFaqs'
import { FEATURED_GUIDES, GUIDE_COUNT } from '@/data/homeIndex'
import { TOOL_COUNT } from '@/data/toolCount'

/*
 * The ten tools. Every card keeps its existing URL. Seven of the ten live inside
 * the builder rather than on a page of their own, and the card says so, so a
 * visitor is not surprised to land in the editor.
 */
const TOOLS: { to: string; icon: typeof CheckCircle2; title: string; body: string }[] = [
  { to: '/ats-resume-checker', icon: CheckCircle2, title: 'ATS Resume Checker', body: 'Scores your resume out of 100 across six categories and lists the fixes in order of impact.' },
  { to: '/job-description-matcher', icon: Target, title: 'Job Description Matcher', body: 'Shows your match percentage and the skills a posting asks for that your resume does not mention.' },
  { to: '/resume-bullet-improver', icon: Sparkles, title: 'Bullet Point Improver', body: 'Flags weak verbs, passive voice, filler and missing numbers, and suggests a rewrite where it safely can.' },
  { to: '/resume-builder', icon: TrendingUp, title: 'Achievement Quantifier', body: 'Offers the kinds of numbers a bullet could carry, as templates. It never invents your figures.' },
  { to: '/resume-builder', icon: FileText, title: 'Summary Builder', body: 'Drafts a summary from your own experience and dates, and critiques the one you wrote.' },
  { to: '/resume-builder', icon: ListChecks, title: 'Completeness Checker', body: 'Works out your career stage and names the sections you are missing for it.' },
  { to: '/resume-builder', icon: Repeat, title: 'Duplicate Detector', body: 'Finds near-identical bullets, repeated phrases, overused verbs and the same figure used twice.' },
  { to: '/resume-builder', icon: Ruler, title: 'Length Advisor', body: 'Estimates your page count and says what to cut, shorten or merge for your experience level.' },
  { to: '/resume-builder', icon: Layers, title: 'Plain Text ATS Preview', body: 'Shows your resume as the plain, linear text a parser works with, and lists formatting that may not survive.' },
  { to: '/resume-builder', icon: FileSignature, title: 'Cover Letter Assistant', body: 'Drafts a cover letter from your resume for the role and company you paste in.' },
]

/* Each one checked against the product, not the mockup: there is no account
   system, the analysis is client-side JavaScript, and the export has no paywall. */
const TRUST_POINTS = [
  { icon: Lock, title: 'Private by design', body: 'Your resume is analyzed in your browser, not uploaded.' },
  { icon: Zap, title: 'Instant results', body: 'No queue and no wait. In the builder, scores update as you type.' },
  { icon: Gift, title: 'Free in full', body: 'Every tool and the PDF export. No watermark.' },
  { icon: UserRoundX, title: 'No account', body: 'Open a tool and start. No sign-up, no email.' },
]

const STEPS = [
  {
    icon: PenLine,
    title: 'Add or create your resume',
    body: 'Start in the builder from scratch or from the sample, or paste or upload a PDF, DOCX or TXT on a tool page.',
    to: '/resume-builder',
    cta: 'Open the builder',
  },
  {
    icon: ScanSearch,
    title: 'Check it and read the findings',
    body: 'The ATS checker scores six categories and names the rule behind every point it takes off.',
    to: '/ats-resume-checker',
    cta: 'Open the ATS checker',
  },
  {
    icon: Download,
    title: 'Improve it and export',
    body: 'Apply the suggested rewrites, tailor a copy to a job posting, then download a PDF with real, selectable text.',
    to: '/how-it-works',
    cta: 'See the full walkthrough',
  },
]

const SCORING_CARDS = [
  {
    title: 'Deterministic rules, not a model',
    body: 'Every finding comes from a rule someone wrote and someone else can review. The same resume always produces the same score, and every suggestion has a stated reason.',
  },
  {
    title: 'How job matching works',
    body: 'The Job Description Matcher uses a hand-built skills list with alternate spellings, so "K8s" matches "Kubernetes". Skills in a posting’s requirements count 1.5 times as much, and repeating a keyword has a capped effect.',
  },
  {
    title: 'It never invents your numbers',
    body: 'The achievement engine classifies what a bullet describes and offers metric shapes with blanks for you to fill. A made-up figure is one you could not defend in an interview.',
  },
  {
    title: 'Real text in the PDF',
    body: 'The export draws actual text rather than a picture of it. Open the PDF and you can select, search and copy every line, which is what a parser does.',
  },
]

/* Section rhythm: one container width (the header's, so edges line up) and
   one vertical step for every band. */
const CONTAINER = 'mx-auto w-full max-w-7xl px-4'
const BAND = 'py-14 sm:py-16'

const SectionHeading = ({ id, title, intro }: { id: string; title: string; intro?: string }) => (
  <div className="mx-auto max-w-2xl text-center">
    <h2 id={id} className="text-balance text-2xl font-bold tracking-tight sm:text-3xl">
      {title}
    </h2>
    {intro && <p className="mt-2 text-balance text-base-content/75">{intro}</p>}
  </div>
)

export const HomePage = () => {
  useSeo()

  return (
    /* `wide`: the sections below set their own container, so bands can run
       edge to edge while the content stays on the header's 80rem grid. */
    <Layout wide>
      <section aria-labelledby="hero-heading" className="border-b border-base-300">
        <div className={`${CONTAINER} grid items-center gap-10 py-10 sm:py-14 lg:grid-cols-[1.05fr_1fr] lg:gap-12`}>
          <div>
            <p className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              <ShieldCheck size={13} aria-hidden="true" /> Free · No sign-up · Your resume stays in your browser
            </p>
            {/*
              The H1 keeps "ATS resume checker and builder", the query the home
              page title is built on (see SITE.tagline), and adds what the tool
              does for you. The mockup's "gets past ATS filters" was not used:
              no tool can promise that, and the FAQ below says so.
            */}
            <h1 id="hero-heading" className="mt-4 text-balance text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl">
              Free ATS resume checker and builder that shows you what to fix
            </h1>
            <p className="mt-4 max-w-xl text-lg text-base-content/80">
              Paste your resume or upload a PDF, DOCX or TXT file. The checker scores what an
              applicant tracking system (ATS) and a recruiter look for, and explains how to fix each
              problem. The builder and its PDF export are free too.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/resume-builder" className="btn btn-primary btn-lg gap-2">
                Build my resume <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <Link to="/ats-resume-checker" className="btn btn-outline btn-lg">
                Check my ATS score
              </Link>
            </div>
            <p className="mt-3 text-sm">
              <Link to="/ats-resume-checker#try-sample" className="link link-primary inline-flex min-h-9 items-center gap-1">
                <CheckCircle2 size={15} aria-hidden="true" /> Or try the sample resume first
              </Link>
            </p>
            <ul className="mt-6 grid gap-4 sm:grid-cols-2">
              {TRUST_POINTS.map(({ icon: Icon, title, body }) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon size={18} aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold">{title}</span>
                    <span className="block text-sm text-base-content/75">{body}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <LocalUsage />
            </div>
          </div>
          <HeroPreview />
        </div>
      </section>

      {/* Names, not logos: see the licensing note in CompanyShowcase.tsx. */}
      <div className="border-b border-base-300 bg-base-100">
        <CompanyShowcase className={`${CONTAINER} py-10`} />
      </div>

      <section aria-labelledby="how-heading" className={BAND}>
        <div className={CONTAINER}>
          <SectionHeading
            id="how-heading"
            title="How to check and improve your resume"
            intro="Three steps. No account, and your resume is never sent to a server."
          />
          <ol className="mt-8 grid gap-4 md:grid-cols-3">
            {STEPS.map(({ icon: Icon, title, body, to, cta }, index) => (
              <li key={title} className="flex flex-col rounded-2xl border border-base-300 bg-base-100 p-5">
                <div className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary" aria-hidden="true">
                    {index + 1}
                  </span>
                  <Icon size={22} className="text-primary" aria-hidden="true" />
                </div>
                <h3 className="mt-4 font-semibold">{title}</h3>
                <p className="mt-1 flex-1 text-sm text-base-content/75">{body}</p>
                <Link to={to} className="link link-primary mt-3 inline-flex min-h-9 items-center gap-1 text-sm font-medium">
                  {cta} <ArrowRight size={14} aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section aria-labelledby="tools-heading" className={`${BAND} border-y border-base-300 bg-base-100`}>
        <div className={CONTAINER}>
          <SectionHeading
            id="tools-heading"
            title={`${TOOL_COUNT} free resume tools, one resume`}
            intro="Each tool exists because of a specific, common reason resumes get filtered out. All of them read the same resume."
          />
          <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {TOOLS.map(({ to, icon: Icon, title, body }) => (
              <li key={title}>
                <Link
                  to={to}
                  className="lift group flex h-full flex-col rounded-2xl border border-base-300 bg-base-100 p-4 hover:border-primary"
                >
                  <Icon size={20} className="text-primary" aria-hidden="true" />
                  <h3 className="mt-3 font-semibold group-hover:text-primary">{title}</h3>
                  <p className="mt-1 flex-1 text-sm text-base-content/75">{body}</p>
                  <p className="mt-3 flex items-center gap-1 text-xs font-medium text-base-content/70">
                    {to === '/resume-builder' ? 'Inside the builder' : 'Standalone page'}
                    <ArrowRight size={13} className="text-primary" aria-hidden="true" />
                  </p>
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-center">
            <Link to="/tools" className="link link-primary inline-flex min-h-9 items-center gap-1 font-medium">
              Compare all {TOOL_COUNT} tools <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </p>
        </div>
      </section>

      {/*
        Replaces the old "Start with the sample" box. The example is the Bullet
        Improver's real output for a line from the sample resume, so a visitor
        sees what the tool says before deciding to use it.
      */}
      <section aria-labelledby="example-heading" className={`${BAND} bg-primary/5`}>
        <div className={`${CONTAINER} grid items-center gap-10 lg:grid-cols-[1fr_1.1fr]`}>
          <div>
            <h2 id="example-heading" className="text-balance text-2xl font-bold tracking-tight sm:text-3xl">
              See how a weak resume bullet gets fixed
            </h2>
            <p className="mt-3 max-w-lg text-base-content/80">
              Most rejected resumes are not written by unqualified people. They are written by
              qualified people who described their work in a way that carries no signal. This is
              what the Bullet Improver says about one line from the built-in sample resume.
            </p>
            <p className="mt-3 max-w-lg text-base-content/80">
              It names the problem, explains it and proposes a stronger verb. The result and the
              number have to come from you, because only you know them.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/ats-resume-checker#try-sample" className="btn btn-primary gap-2">
                Check the sample resume <ArrowRight size={17} aria-hidden="true" />
              </Link>
              <Link to="/resume-bullet-improver" className="btn btn-outline">
                Try your own bullet
              </Link>
            </div>
          </div>
          <BulletExample />
        </div>
        <div className={`${CONTAINER} mt-12`}>
          <h3 className="text-lg font-semibold">Two more before-and-after examples</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 [&>*]:bg-base-100">
            <BeforeAfter
              before="Worked on React Project"
              after="Rebuilt the patient booking flow in React and TypeScript, increasing completed bookings by 23% in one quarter"
              note="Weak verb, no ownership, no outcome, no number: four separate problems in four words."
            />
            <BeforeAfter
              before="Responsible for managing the reporting process"
              after="Automated the weekly reporting workflow in Python, saving the team 15 hours per week"
              note="A duty becomes an achievement. The tool flags the duty phrasing and the missing outcome. The outcome and the number have to come from you."
            />
          </div>
          <p className="mt-3 text-sm text-base-content/70">
            Illustrative examples written for this page. The figures in the &ldquo;after&rdquo;
            versions are made up to show the shape of a strong bullet. The tools never add numbers
            for you.
          </p>
        </div>
      </section>

      {/*
        Every sentence here matches /privacy and the consent banner as of
        2026-10-21. It deliberately does not say "nothing leaves your device":
        analytics and advertising scripts do load, and the privacy page lists
        them. What never leaves is the resume text.
      */}
      <section aria-labelledby="privacy-heading" className={BAND}>
        <div className={CONTAINER}>
          <SectionHeading
            id="privacy-heading"
            title="What happens to your resume"
            intro="There is no server that receives your resume. Here is exactly where it goes."
          />
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Lock,
                title: 'Analyzed in your browser',
                body: 'Every tool is JavaScript running on your device. Reading an uploaded PDF or DOCX happens there too.',
              },
              {
                icon: HardDrive,
                title: 'Saved only in this browser',
                body: 'A resume in the builder is kept in local storage. Text pasted on a tool page stays in that tab until you reload or close it.',
              },
              {
                icon: Download,
                title: 'Exported on your device',
                body: 'The PDF is generated in the browser. Clearing site data deletes your resume, so keep a JSON backup from the builder.',
              },
              {
                icon: ShieldCheck,
                title: 'Analytics and ads never see it',
                body: 'This site uses analytics and advertising services. None of them receives anything you type into a resume.',
              },
            ].map(({ icon: Icon, title, body }) => (
              <li key={title} className="rounded-2xl border border-base-300 bg-base-100 p-5">
                <span className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon size={18} aria-hidden="true" />
                </span>
                <h3 className="mt-3 font-semibold">{title}</h3>
                <p className="mt-1 text-sm text-base-content/75">{body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm">
            <Link to="/privacy" className="link link-primary inline-flex min-h-9 items-center">
              Read the privacy policy and the list of services
            </Link>
            <CookieChoicesButton className="link link-primary inline-flex min-h-9 items-center" />
          </p>
        </div>
      </section>

      <section className={`${BAND} border-y border-base-300 bg-base-100`}>
        <div className={CONTAINER}>
          <PricingComparison heading="What you get for free" />
        </div>
      </section>

      {/*
        Where the mockup has testimonials. There are no verified testimonials to
        show, so this space goes to what a visitor can check for themselves: how
        the scoring works, the experiments behind it, and its limits.
      */}
      <section aria-labelledby="scoring-heading" className={BAND}>
        <div className={CONTAINER}>
          <SectionHeading
            id="scoring-heading"
            title="How the ATS score works"
            intro={`The ATS Resume Checker runs ${RULE_CHECKS} fixed rules, grouped into six weighted categories that add up to 100 points. There is no AI model and no unexplained number.`}
          />
          <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SCORE_CATEGORIES.map((category) => (
              <li key={category.label} className="rounded-2xl border border-base-300 bg-base-100 p-4">
                <p className="flex items-baseline justify-between gap-3">
                  <span className="font-semibold">{category.label}</span>
                  <span className="shrink-0 text-sm font-medium tabular-nums text-primary">{category.max} points</span>
                </p>
                <p className="mt-1 text-sm text-base-content/75">{category.body}</p>
              </li>
            ))}
          </ul>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {SCORING_CARDS.map((card) => (
              <div key={card.title} className="rounded-2xl border border-base-300 bg-base-100 p-5">
                <h3 className="font-semibold">{card.title}</h3>
                <p className="mt-1 text-sm text-base-content/80">{card.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Link to="/methodology" className="lift group rounded-2xl border border-base-300 bg-base-100 p-5 hover:border-primary">
              <h3 className="font-semibold group-hover:text-primary">Methodology</h3>
              <p className="mt-1 text-sm text-base-content/75">
                The six scoring categories and their weights, the PDF parsing thresholds, and the
                checks the tool does not attempt.
              </p>
            </Link>
            <Link to="/evidence" className="lift group rounded-2xl border border-base-300 bg-base-100 p-5 hover:border-primary">
              <h3 className="font-semibold group-hover:text-primary">Parsing experiments</h3>
              <p className="mt-1 text-sm text-base-content/75">
                Controlled comparisons against this site&apos;s own parser: one column against two,
                contact icons on and off, standard against unusual headings.
              </p>
            </Link>
            <Link to="/how-it-works" className="lift group rounded-2xl border border-base-300 bg-base-100 p-5 hover:border-primary">
              <h3 className="font-semibold group-hover:text-primary">Step-by-step walkthrough</h3>
              <p className="mt-1 text-sm text-base-content/75">
                What each tool reads, what it checks and what it shows you, using the sample resume.
              </p>
            </Link>
          </div>
          <div className="mt-8 rounded-2xl border border-base-300 bg-base-200 p-5">
            <h3 className="font-semibold">What this cannot promise you</h3>
            <p className="mt-1 text-sm text-base-content/80">
              No tool, this one included, can guarantee an interview or replicate a specific
              employer&apos;s hiring system. Every deployment is configured differently, and none of
              them publish their ranking logic. What these tools can do is remove the mechanical
              reasons a resume gets missed: unparseable formatting, missing keywords, unquantified
              bullets. That is a real, controllable part of the outcome. It is not the whole of it.
            </p>
          </div>
        </div>
      </section>

      <div className={`${CONTAINER} max-w-3xl`}>
        <FaqSection faqs={STATIC_FAQS['/']} />
      </div>

      <section aria-labelledby="guides-heading" className={BAND}>
        <div className={CONTAINER}>
          <SectionHeading
            id="guides-heading"
            title="Resume guides"
            intro={`${GUIDE_COUNT} practical guides, written from the same rules the tools apply.`}
          />
          {/* The ATS-friendly guide is linked here rather than as a card, so the
              six cards can go to the guides with the fewest other inbound links. */}
          <p className="mt-2 text-center text-sm">
            <span className="text-base-content/75">New here? Start with the </span>
            <Link to="/guides/ats-friendly-resume" className="link link-primary">ATS-friendly resume guide</Link>
            <span className="text-base-content/75">.</span>
          </p>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURED_GUIDES.map((guide) => (
              <li key={guide.slug}>
                <Link
                  to={`/guides/${guide.slug}`}
                  className="lift group flex h-full flex-col rounded-2xl border border-base-300 bg-base-100 p-5 hover:border-primary"
                >
                  <h3 className="font-semibold group-hover:text-primary">{guide.h1}</h3>
                  <p className="mt-1 line-clamp-3 text-sm text-base-content/75">{guide.description}</p>
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-center">
            <Link to="/guides" className="link link-primary inline-flex min-h-9 items-center gap-1 font-medium">
              View all guides <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </p>
        </div>
      </section>

      <div className={CONTAINER}>
        <SponsorSlot />
      </div>

      <section aria-labelledby="final-cta-heading" className={`${CONTAINER} mb-4`}>
        {/* A white surface, not an emerald band: emerald is reserved for
            interactive elements (docs/design.md, "Where colour lives"), so the
            filled button stays the one green thing in this box. */}
        <div className="rounded-3xl border border-base-300 bg-base-100 px-6 py-12 text-center sm:px-12">
          <h2 id="final-cta-heading" className="text-balance text-2xl font-bold tracking-tight sm:text-3xl">
            Make your next resume easier to improve
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-base-content/80">
            Start a new one in the builder, or check the resume you already have. Free, with no account.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/resume-builder" className="btn btn-primary btn-lg gap-2">
              Build my resume <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <Link to="/ats-resume-checker" className="btn btn-outline btn-lg">
              Check my ATS score
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default HomePage
